import { useParams, Link, Navigate } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getDepartments, getNotices, getGallery } from "services/content";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";
import { mediaUrl } from "lib/media";
import { useSlideshow, layerClass, resolveSlideText, slideShowsText, shadeCss } from "lib/slider";
import SlideImage from "components/shared/SlideImage";

/**
 * One honours department's own page (/departments/:slug), styled as an
 * editorial university brochure: split hero with a highlight card, stat banner
 * with captions, numbered eyebrow sections, pillars beside the About text, a
 * themed year-by-year curriculum, showcase, facilities with equipment chips,
 * faculty, gallery, noticeboard and an admissions call-to-action.
 *
 * Every field comes from the `departments` blob (admin → Departments). All the
 * richer fields are optional — a department with only the basics still renders
 * cleanly, and empty sections are skipped rather than shown as empty shells.
 */

const initials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/* Title with the last word set in italic gold, e.g. "Fashion *Design*". */
function AccentTitle({ text }) {
  const words = String(text || "").trim().split(" ");
  if (words.length < 2) return <>{text}</>;
  const last = words.pop();
  return (
    <>
      {words.join(" ")} <span className="italic font-normal text-gold-light">{last}</span>
    </>
  );
}

/* Eyebrow + heading: "01 —— LEGACY & VISION" then the H2 and a sub line. */
function SectionHead({ id, index, eyebrow, title, sub, color, right }) {
  return (
    <div id={id} className="scroll-mt-[150px] mb-8">
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono text-[13px] font-bold" style={{ color }}>
          {String(index).padStart(2, "0")}
        </span>
        <span className="h-px w-8" style={{ background: color }} />
        <span className="text-[11px] uppercase tracking-[0.18em] text-muted font-semibold">
          {eyebrow}
        </span>
      </div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-[28px] sm:text-[36px] font-bold text-green-dark leading-tight">
            {title}
          </h2>
          {sub && <p className="text-muted text-[14px] mt-1.5">{sub}</p>}
        </div>
        {right}
      </div>
    </div>
  );
}

const Card = ({ className = "", children, style }) => (
  <div
    className={`bg-white rounded-2xl border border-line shadow-[0_4px_18px_rgba(0,0,0,0.05)] ${className}`}
    style={style}
  >
    {children}
  </div>
);

export default function DepartmentPage() {
  const { slug } = useParams();
  const { data, loading } = useResource(getDepartments);
  const { data: allNotices } = useResource(getNotices);
  const { data: allPhotos } = useResource(getGallery);

  const items = data?.items || [];
  const dept = items.find((d) => d.slug === slug);

  if (loading) {
    return (
      <div className="max-w-container mx-auto px-4 sm:px-10 py-12 grid gap-5">
        <Skeleton className="h-[360px] rounded-3xl" />
        <Skeleton className="h-[140px] rounded-2xl" />
      </div>
    );
  }
  if (!dept) return <Navigate to="/" replace />;

  return <DepartmentContent dept={dept} allNotices={allNotices} allPhotos={allPhotos} />;
}

/** The page body, split out so the banner slideshow hook can run unconditionally. */
function DepartmentContent({ dept, allNotices, allPhotos }) {
  // Banner slider: `slides[]` from admin → Departments → Banner photos, with the
  // same per-slide options as every other slider (lib/slider.js). A department
  // with only the legacy single `heroImage` behaves as a one-photo slider.
  const slides = (dept.slides || []).filter((s) => s.image || s.mobileImage || s.headline || s.textFromPrevious);
  if (!slides.length && dept.heroImage) slides.push({ image: dept.heroImage });
  const show = useSlideshow(slides, { defaultDuration: 6 });
  const slide = slides[show.cur];
  const { text: slideText, key: slideTextKey } = resolveSlideText(slides, show.cur);
  const hasSlideText = !!slide && slideShowsText(slide) && (slideText.headline || slideText.sub);

  const color = dept.color || "#9B7200";
  const head = dept.head || {};
  const faculty = (dept.faculty || []).filter((f) => (f.name || "").trim());
  const pillars = (dept.pillars || []).filter((p) => (p.title || "").trim());
  const showcase = (dept.showcase || []).filter((s) => (s.title || "").trim());
  const curriculum = (dept.curriculum || []).filter((y) => (y.courses || []).length);
  const facilities = dept.facilities || [];
  const events = dept.events || [];
  const contact = dept.contact || {};
  const hasContact = contact.email || contact.phone || contact.office;
  const hl = dept.highlight || {};
  const hasHighlight = hl.title || hl.quote;
  const notices = (allNotices || [])
    .filter((n) => n.tags?.includes(dept.code) || n.tags?.includes(dept.slug))
    .slice(0, 4);
  const photos = (allPhotos || [])
    .filter((ph) => ph.tags?.includes(dept.code) || ph.tags?.includes(dept.slug))
    .slice(0, 5);
  const syllabus = (dept.programmes || []).find((p) => p.syllabusUrl)?.syllabusUrl;
  const instHref = `/institutions/${dept.institution || "honors"}`;
  const applyHref = dept.applyHref || "/admissions/honors";
  const cta = dept.cta || {};
  const creditsOf = (yr) =>
    yr.courses.reduce((n, c) => n + (parseFloat(c.credits) || 0), 0);

  const sections = [
    dept.about && ["about", "Overview"],
    dept.programmes?.length && ["programmes", "Programmes"],
    showcase.length && ["showcase", "Showcase"],
    (curriculum.length || dept.courses?.length) && ["curriculum", "Curriculum"],
    facilities.length && ["facilities", "Facilities"],
    faculty.length && ["faculty", "Faculty"],
    photos.length && ["gallery", "Gallery"],
    (notices.length || events.length || hasContact) && ["contact", "Contact"],
  ].filter(Boolean);
  const num = (id) => sections.findIndex(([s]) => s === id) + 1;

  return (
    <div className="animate-fadeIn bg-cream">
      {/* ================================================================ Hero */}
      <section
        className="relative overflow-hidden px-4 sm:px-10 py-16 sm:py-20 border-b-4 border-gold"
        style={{ background: `linear-gradient(120deg,#042217 0%,${color} 110%)` }}
        aria-roledescription={slides.length > 1 ? "carousel" : undefined}
      >
        {/* Banner photos: one animated layer per slide (see lib/slider.js).
            Photos show clean by default; the optional per-slide shade is what
            dims them when the title needs contrast. */}
        {slides.map((sl, i) => {
          if (!sl.image && !sl.mobileImage) return null;
          const sh = shadeCss(sl.shade);
          return (
            <div
              key={`${sl.image}-${sl.mobileImage}-${i}`}
              aria-hidden="true"
              className={layerClass(i, show, sl.animation, "pointer-events-none")}
              style={{ "--sl-dir": show.dir }}
            >
              {/* Desktop photo, swapped for the mobile upload under 768px. */}
              <SlideImage slide={sl} />
              {sh && <div className="absolute inset-0" style={{ background: sh }} />}
            </div>
          );
        })}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff 0 2px,transparent 2px 10px)" }}
        />
        <div aria-hidden="true" className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full border border-gold/20 pointer-events-none" />
        <div aria-hidden="true" className="absolute -right-10 -top-10 w-[340px] h-[340px] rounded-full border border-dashed border-gold/25 pointer-events-none" />

        <div className="max-w-container mx-auto relative z-[2]">
          <div className="text-[12px] text-white/55 mb-6 font-mono">
            <Link to="/" className="text-white/55 no-underline hover:text-white">Home</Link>
            <span className="mx-2 text-gold">›</span>
            <Link to={instHref} className="text-white/55 no-underline hover:text-white">Honours</Link>
            <span className="mx-2 text-gold">›</span>
            <span className="text-white font-bold">{dept.name}</span>
          </div>

          <div className={`grid gap-12 items-center ${hasHighlight ? "lg:grid-cols-12" : ""}`}>
            <div className={`${hasHighlight ? "lg:col-span-7" : "max-w-[760px]"} flex flex-col gap-6`}>
              {dept.kicker && (
                <div className="inline-flex w-fit items-center gap-2 px-3.5 py-1.5 rounded-full bg-black/30 border border-gold/40 text-gold-light text-[11px] font-semibold uppercase tracking-[0.16em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                  {dept.kicker}
                </div>
              )}
              <h1 className="font-serif font-bold text-white leading-[1.06] text-[clamp(38px,5.4vw,68px)]">
                <AccentTitle text={dept.name} />
              </h1>
              {/* A slide with its own text replaces the tagline while it is
                  showing; slides with "show text" off fall back to the tagline. */}
              {hasSlideText ? (
                <div key={slideTextKey} className="sl-cap">
                  {slideText.headline && (
                    <p className="text-white/85 text-[17px] sm:text-[20px] font-light max-w-[600px] leading-relaxed whitespace-pre-line">
                      {slideText.headline}
                    </p>
                  )}
                  {slideText.sub && (
                    <p className="text-white/65 text-[14px] max-w-[600px] leading-relaxed mt-2">
                      {slideText.sub}
                    </p>
                  )}
                </div>
              ) : (
                dept.tagline && (
                  <p className="text-white/80 text-[17px] sm:text-[20px] font-light max-w-[600px] leading-relaxed">
                    {dept.tagline}
                  </p>
                )
              )}

              <div className="flex flex-wrap items-center gap-3 pt-1">
                {dept.programmes?.length > 0 && (
                  <a href="#programmes" className="px-6 py-3.5 rounded-full bg-gold-light text-green-dark font-bold text-[12.5px] uppercase tracking-wider no-underline shadow-lg transition-transform hover:-translate-y-0.5">
                    Explore Programmes ↓
                  </a>
                )}
                {syllabus && (
                  <a href={mediaUrl(syllabus) || syllabus} target="_blank" rel="noreferrer" className="px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/15 text-white border border-gold/30 font-medium text-[13px] no-underline backdrop-blur-sm">
                    Download Syllabus (PDF)
                  </a>
                )}
                {facilities.length > 0 && (
                  <a href="#facilities" className="px-5 py-3.5 rounded-full hover:bg-white/10 text-white/85 font-medium text-[13px] no-underline">
                    Studio &amp; Lab Tour →
                  </a>
                )}
              </div>

              {dept.footnote && (
                <div className="pt-4 border-t border-white/15 text-[12.5px] text-white/60 max-w-[600px]">
                  ✦ {dept.footnote}
                </div>
              )}

              {slides.length > 1 && (
                <div className="flex gap-2 pt-1">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => show.go(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      aria-current={i === show.cur ? "true" : undefined}
                      className="h-[7px] rounded-full border-none cursor-pointer transition-all duration-300 p-0"
                      style={{
                        width: i === show.cur ? 26 : 7,
                        background: i === show.cur ? "#f7efc8" : "rgba(255,255,255,0.35)",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {hasHighlight && (
              <div className="lg:col-span-5">
                <div className="relative mx-auto max-w-md bg-black/35 p-6 rounded-2xl border border-gold/30 shadow-2xl backdrop-blur-md">
                  <div className="flex justify-between items-center pb-4 border-b border-white/10">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose" />
                      <span className="w-2.5 h-2.5 rounded-full bg-gold" />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-mid" />
                    </div>
                    <span className="font-mono text-[11px] text-gold uppercase tracking-widest">{hl.label || "Featured"}</span>
                  </div>
                  <div className="my-5 rounded-xl p-5 border border-dashed border-gold/40 bg-white/5">
                    {hl.eyebrow && <span className="text-[11px] uppercase tracking-wider text-gold-light font-mono block">{hl.eyebrow}</span>}
                    {hl.title && <h3 className="text-[20px] font-serif font-bold text-white mt-1">{hl.title}</h3>}
                    {hl.by && <p className="text-[12px] text-white/70 mt-0.5">{hl.by}</p>}
                    {hl.tags?.length > 0 && (
                      <div className="grid grid-cols-3 gap-2.5 my-4">
                        {hl.tags.slice(0, 3).map((t) => (
                          <div key={t} className="bg-black/30 px-2 py-2.5 rounded text-center border border-white/10 text-[11px] text-gold font-mono">
                            {t}
                          </div>
                        ))}
                      </div>
                    )}
                    {hl.quote && (
                      <div className="p-3 bg-black/30 rounded-lg border border-white/10 text-[12.5px] text-white/80 italic">
                        “{hl.quote}”
                      </div>
                    )}
                  </div>
                  {hl.footer && (
                    <div className="text-[12px] text-white/60 flex items-center justify-between">
                      <span>✓ {hl.footer}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ======================================================== Stat banner */}
      {dept.stats?.length > 0 && (
        <div className="relative -mt-8 z-[3] px-4 sm:px-10">
          <div className="max-w-[1100px] mx-auto">
            <Card className="p-6 sm:p-8 shadow-[0_20px_50px_rgba(20,30,20,0.12)]">
              <div className={`grid grid-cols-2 gap-6 md:gap-8 md:divide-x divide-line ${dept.stats.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
                {dept.stats.slice(0, 4).map((s, i) => (
                  <div key={`${s.label}-${i}`} className="text-center md:pl-6 first:pl-0">
                    <span className="font-serif text-[36px] sm:text-[46px] font-bold text-green-dark tracking-tight block leading-none">
                      {s.value}
                    </span>
                    <span className="text-[11px] uppercase font-bold tracking-[0.16em] text-ink mt-2 block">{s.label}</span>
                    {s.note && <span className="text-[11px] text-muted mt-1 block">{s.note}</span>}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ======================================================= Sticky nav */}
      {sections.length > 1 && (
        <nav aria-label="Sections" className="sticky top-[70px] z-[500] bg-cream/90 backdrop-blur border-b border-line py-3 mt-8 px-4 sm:px-10">
          <div className="max-w-[1100px] mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto text-[11.5px] font-semibold uppercase tracking-wider">
              {sections.map(([id, label], i) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="whitespace-nowrap text-muted no-underline pb-1 border-b-2 border-transparent transition-colors hover:text-green-dark"
                  onMouseEnter={(e) => (e.currentTarget.style.borderBottomColor = color)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderBottomColor = "transparent")}
                >
                  <span className="font-mono mr-1" style={{ color }}>{String(i + 1).padStart(2, "0")}</span>
                  {label}
                </a>
              ))}
            </div>
            <Link to={instHref} className="hidden md:inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-green-dark no-underline border border-green-dark/30 px-3 py-1.5 rounded-full hover:border-gold transition-colors">
              ▦ All honours departments
            </Link>
          </div>
        </nav>
      )}

      <div className="max-w-[1100px] mx-auto px-4 sm:px-10 py-16 flex flex-col gap-24">
        {/* ============================================================ About */}
        {dept.about && (
          <section>
            <SectionHead id="about" index={num("about")} eyebrow="Legacy & Vision" title="About the Department" color={color} />
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7 flex flex-col gap-6">
                <p className="text-[16px] sm:text-[17px] text-ink leading-[1.85] first-letter:float-left first-letter:font-serif first-letter:text-[62px] first-letter:leading-[0.82] first-letter:font-bold first-letter:pr-3 first-letter:pt-1 first-letter:text-green-dark">
                  {dept.about}
                </p>
                {dept.aboutMore && (
                  <p className="text-muted text-[14.5px] leading-[1.8]">{dept.aboutMore}</p>
                )}
                {pillars.length > 0 && (
                  <div className="grid sm:grid-cols-3 gap-4 pt-2">
                    {pillars.slice(0, 3).map((p, i) => (
                      <Card key={`${p.title}-${i}`} className="p-4 hover:border-gold/60 transition-colors">
                        <div className="text-[20px] leading-none mb-2">{p.icon || "✦"}</div>
                        <h4 className="font-serif font-bold text-[14px] text-green-dark">{p.title}</h4>
                        {p.desc && <p className="text-[12px] text-muted mt-1 leading-snug">{p.desc}</p>}
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {(head.name || head.message) && (
                <div className="lg:col-span-5">
                  <Card className="p-6 sm:p-7 relative overflow-hidden">
                    <div aria-hidden="true" className="absolute top-0 right-0 w-32 h-32 rounded-bl-full pointer-events-none" style={{ background: `${color}12` }} />
                    <div className="relative">
                      <div className="flex items-center gap-4 mb-5">
                        {mediaUrl(head.photo) ? (
                          <img src={mediaUrl(head.photo)} alt={head.name || "Head of the Department"} className="w-20 h-20 rounded-2xl object-cover border-2 border-gold shadow-md shrink-0" />
                        ) : (
                          /* Placeholder portrait until a photo is uploaded in admin */
                          <div
                            className="w-20 h-20 rounded-2xl border-2 border-gold shadow-md shrink-0 flex items-end justify-center overflow-hidden"
                            style={{ background: `${color}14` }}
                            aria-label="Photo placeholder"
                          >
                            <svg viewBox="0 0 64 64" className="w-16 h-16" aria-hidden="true">
                              <circle cx="32" cy="22" r="12" fill={color} opacity="0.35" />
                              <path d="M8 62c2-14 12-20 24-20s22 6 24 20z" fill={color} opacity="0.35" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <span className="text-[10.5px] uppercase tracking-[0.16em] text-gold font-bold block">{head.role || "Head of the Department"}</span>
                          <h3 className={`font-serif font-bold text-[18px] ${head.name ? "text-green-dark" : "text-muted italic"}`}>
                            {head.name || "Name to be added"}
                          </h3>
                          {head.qualification && <p className="text-[12px] text-muted">{head.qualification}</p>}
                        </div>
                      </div>
                      {head.message && (
                        <blockquote className="italic font-serif text-green-dark text-[17px] sm:text-[19px] leading-snug border-l-2 border-gold pl-4 py-1 m-0">
                          “{head.message}”
                        </blockquote>
                      )}
                      {head.bio && <p className="text-[12.5px] text-muted leading-relaxed mt-4">{head.bio}</p>}
                      {faculty.length > 0 && (
                        <div className="pt-4 mt-5 border-t border-line text-[12px]">
                          <a href="#faculty" className="text-gold font-semibold no-underline hover:underline">Meet the full faculty →</a>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ======================================================= Programmes */}
        {dept.programmes?.length > 0 && (
          <section>
            <SectionHead
              id="programmes"
              index={num("programmes")}
              eyebrow="Degree Offerings"
              title="Programmes Offered"
              sub={dept.kicker ? `Honours curriculum · ${dept.kicker}` : undefined}
              color={color}
              right={
                dept.admissionsStatus && (
                  <span className="inline-flex items-center gap-2 bg-green-light text-green-dark px-3 py-1.5 rounded-full text-[12px] font-semibold border border-green/20">
                    <span className="w-2 h-2 rounded-full bg-green animate-pulse" />
                    {dept.admissionsStatus}
                  </span>
                )
              }
            />
            <div className="flex flex-col gap-6">
              {dept.programmes.map((p, i) => {
                const facts = [
                  ["Academic level", p.level],
                  ["Duration", p.duration],
                  ["Total credits", p.credits],
                  ["Instruction", p.medium],
                  ["Approved intake", p.seats],
                ].filter(([, v]) => v);
                return (
                  <Reveal key={`${p.name}-${i}`} delay={i * 60}>
                    <Card className="rounded-3xl p-6 sm:p-10 relative overflow-hidden">
                      <div aria-hidden="true" className="absolute -right-12 -top-12 w-32 h-32 rotate-45 flex items-end justify-center pb-2" style={{ background: color }}>
                        <span className="text-gold-light text-[14px] mb-1">★</span>
                      </div>
                      <div className="max-w-[860px] flex flex-col gap-6">
                        <div>
                          <span className="text-[11px] font-mono uppercase tracking-widest font-bold" style={{ color }}>
                            {p.level ? `${p.level} honours degree` : "Honours degree"}
                          </span>
                          <h3 className="font-serif text-[24px] sm:text-[30px] font-bold text-green-dark mt-1">{p.name}</h3>
                          {p.desc && <p className="text-muted text-[14.5px] mt-2 leading-relaxed">{p.desc}</p>}
                        </div>
                        {facts.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                            {facts.map(([k, v]) => (
                              <div key={k} className="bg-cream p-3.5 rounded-xl border border-line">
                                <span className="text-[10.5px] font-mono text-muted uppercase block">{k}</span>
                                <span className="font-serif font-bold text-[14px] text-green-dark mt-0.5 block">{v}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {p.eligibility && (
                          <div className="rounded-2xl p-5 border-[1.5px] border-dashed flex gap-3 items-start" style={{ borderColor: `${color}66`, background: `${color}08` }}>
                            <div className="p-2 rounded-lg text-gold-light shrink-0" style={{ background: color }}>🎓</div>
                            <div>
                              <h5 className="text-[11px] uppercase tracking-wider font-bold text-green-dark">Eligibility criteria</h5>
                              <p className="text-[13px] text-muted mt-0.5 leading-relaxed">{p.eligibility}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-3">
                          <Link to={applyHref} className="text-white px-6 py-3 rounded-full text-[11.5px] font-bold uppercase tracking-widest no-underline shadow hover:shadow-md" style={{ background: color }}>
                            Apply for honours →
                          </Link>
                          {(curriculum.length > 0 || dept.courses?.length > 0) && (
                            <a href="#curriculum" className="bg-cream hover:bg-[#efe9dc] text-ink px-5 py-3 rounded-full text-[11.5px] font-semibold uppercase tracking-wider no-underline border border-line">
                              View course map
                            </a>
                          )}
                          {p.syllabusUrl && (
                            <a href={mediaUrl(p.syllabusUrl) || p.syllabusUrl} target="_blank" rel="noreferrer" className="text-[12px] font-semibold no-underline" style={{ color }}>
                              Syllabus (PDF) ↓
                            </a>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Reveal>
                );
              })}
            </div>
          </section>
        )}

        {/* ========================================================= Showcase */}
        {showcase.length > 0 && (
          <section>
            <SectionHead id="showcase" index={num("showcase")} eyebrow="Student Work" title="Student Showcase" sub="Selected work from recent batches." color={color} />
            <div className="grid md:grid-cols-3 gap-6">
              {showcase.map((s, i) => (
                <Reveal key={`${s.title}-${i}`} delay={(i % 3) * 70}>
                  <Card className="overflow-hidden flex flex-col hover:shadow-lg transition-all duration-300">
                    <div
                      className="h-52 relative flex items-center justify-center p-6 bg-cover bg-center"
                      style={{
                        background: mediaUrl(s.image) ? undefined : `linear-gradient(140deg,#042217,${color})`,
                        backgroundImage: mediaUrl(s.image) ? `url(${mediaUrl(s.image)})` : "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 0)",
                        backgroundSize: mediaUrl(s.image) ? "cover" : "22px 22px",
                      }}
                    >
                      {!mediaUrl(s.image) && (
                        <div className="w-24 h-24 rounded-full border-2 border-gold/50 flex items-center justify-center text-center p-3">
                          <span className="font-serif italic text-gold-light text-[12px]">{s.tag || s.title}</span>
                        </div>
                      )}
                      {s.tag && <span className="absolute bottom-3 left-3 bg-white/90 text-green-dark px-2.5 py-0.5 rounded text-[10px] font-mono font-bold uppercase">{s.tag}</span>}
                    </div>
                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        {s.eyebrow && <span className="text-[11px] font-mono font-semibold" style={{ color }}>{s.eyebrow}</span>}
                        <h3 className="font-serif font-bold text-[17px] text-green-dark mt-1">{s.title}</h3>
                        {s.desc && <p className="text-[12.5px] text-muted mt-2 leading-relaxed">{s.desc}</p>}
                      </div>
                      {s.by && <div className="pt-4 mt-4 border-t border-line text-[12px] text-muted font-medium">{s.by}</div>}
                    </div>
                  </Card>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ======================================================= Curriculum */}
        {(curriculum.length > 0 || dept.courses?.length > 0) && (
          <section>
            <SectionHead id="curriculum" index={num("curriculum")} eyebrow="Academic Rigour" title="Curriculum & Courses" sub="Year-by-year progression from foundations to the final-year project." color={color} />
            {curriculum.length > 0 ? (
              <div className="flex flex-col gap-6">
                {curriculum.map((yr, yi) => {
                  const last = yi === curriculum.length - 1;
                  const total = creditsOf(yr);
                  return (
                    <Reveal key={`${yr.year}-${yi}`} delay={yi * 50}>
                      <Card className={`p-6 relative overflow-hidden hover:border-gold/50 transition-colors ${last ? "border-2 shadow-md" : ""}`} style={last ? { borderColor: `${color}55` } : undefined}>
                        {last && <div aria-hidden="true" className="absolute top-0 right-0 w-2 h-full bg-gold" />}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-line gap-3">
                          <div className="flex items-center gap-3">
                            <span className="w-9 h-9 rounded-full text-white font-mono text-[14px] font-bold flex items-center justify-center shrink-0" style={{ background: color }}>
                              {yi + 1}
                            </span>
                            <div>
                              <h3 className="font-serif font-bold text-[18px] text-green-dark">
                                {yr.year}{yr.title ? ` • ${yr.title}` : ""}
                              </h3>
                              {yr.focus && <p className="text-[12px] text-muted">Focus: {yr.focus}</p>}
                            </div>
                          </div>
                          <span className="text-[11.5px] font-mono font-semibold px-3 py-1 rounded-full w-fit" style={{ background: last ? color : `${color}12`, color: last ? "#fff" : color }}>
                            {yr.courses.length} module{yr.courses.length === 1 ? "" : "s"}{total ? ` • ${total} credits` : ""}
                          </span>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                          {yr.courses.map((c, ci) => (
                            <div key={`${c.code}-${ci}`} className="p-3.5 rounded-xl bg-cream border border-line">
                              <div className="flex justify-between items-start mb-1.5">
                                <span className="font-mono text-[12px] font-bold text-green-dark">{c.code}</span>
                                {c.credits && <span className="text-[10px] font-bold px-2 py-0.5 rounded text-white" style={{ background: color }}>{c.credits} cr</span>}
                              </div>
                              <h4 className="font-serif font-bold text-[14px] text-green-dark">{c.name}</h4>
                              {c.desc && <p className="text-[12px] text-muted mt-1 leading-snug">{c.desc}</p>}
                              {c.type && (
                                <div className="mt-2.5">
                                  <span className="text-[10px] bg-white border border-line px-2 py-0.5 rounded text-muted">{c.type}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Card>
                    </Reveal>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {dept.courses.map((c, i) => (
                  <div key={`${c}-${i}`} className="flex items-center gap-2.5 bg-white border border-line rounded-xl px-4 py-3 text-[13.5px] font-medium text-ink">
                    <span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: color }} />
                    {c}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ======================================================= Facilities */}
        {facilities.length > 0 && (
          <section>
            <SectionHead
              id="facilities"
              index={num("facilities")}
              eyebrow="Infrastructure"
              title="Facilities & Labs"
              sub="Where the teaching actually happens."
              color={color}
              right={<span className="text-[11.5px] font-mono text-muted bg-[#efe9dc] px-3 py-1 rounded-full">{facilities.length} space{facilities.length === 1 ? "" : "s"}</span>}
            />
            <div className="grid sm:grid-cols-2 gap-6">
              {facilities.map((f, i) => (
                <Reveal key={`${f.name}-${i}`} delay={(i % 2) * 70}>
                  <Card className="p-6 hover:shadow-md transition-shadow h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-[22px] shrink-0 border" style={{ background: `${color}10`, borderColor: `${color}22` }}>
                        {f.icon || "•"}
                      </div>
                      <div>
                        {f.location && <span className="text-[10.5px] font-mono uppercase tracking-wider font-bold block" style={{ color }}>{f.location}</span>}
                        <h3 className="font-serif font-bold text-[17px] text-green-dark">{f.name}</h3>
                      </div>
                    </div>
                    {f.desc && <p className="text-[12.5px] text-muted leading-relaxed mb-4">{f.desc}</p>}
                    {f.equipment?.length > 0 && (
                      <div className="bg-cream p-3 rounded-xl border border-line flex flex-wrap gap-2 text-[11px] font-mono text-green-dark">
                        {f.equipment.map((e) => (
                          <span key={e} className="bg-white px-2 py-0.5 rounded border border-line">{e}</span>
                        ))}
                      </div>
                    )}
                  </Card>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ========================================================== Faculty */}
        {faculty.length > 0 && (
          <section>
            <SectionHead id="faculty" index={num("faculty")} eyebrow="Mentorship" title="Academic & Studio Faculty" sub="The people who teach, supervise and mentor in this department." color={color} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {faculty.map((f, i) => (
                <Reveal key={`${f.name}-${i}`} delay={(i % 3) * 60}>
                  <Card className="p-5 hover:border-gold/60 transition-all h-full flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        {mediaUrl(f.photo) ? (
                          <img src={mediaUrl(f.photo)} alt={f.name} className="w-12 h-12 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-full text-gold-light flex items-center justify-center font-serif font-bold text-[15px] shrink-0" style={{ background: color }}>
                            {initials(f.name)}
                          </div>
                        )}
                        <div>
                          <h4 className="font-serif font-bold text-green-dark text-[15px] leading-snug">{f.name}</h4>
                          {f.role && <p className="text-[12px] text-muted">{f.role}</p>}
                        </div>
                      </div>
                      {f.specialization && <p className="text-[12.5px] text-muted mt-1">Specialization: {f.specialization}</p>}
                    </div>
                    {f.qualification && (
                      <div className="pt-3 mt-3 border-t border-line text-[11px] font-mono text-green-dark">{f.qualification}</div>
                    )}
                  </Card>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ========================================================== Gallery */}
        {photos.length > 0 && (
          <section>
            <SectionHead id="gallery" index={num("gallery")} eyebrow="Campus Life" title="Life in the Department" color={color} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 auto-rows-[130px]">
              {photos.map((ph, i) => (
                <div
                  key={ph.id}
                  title={ph.label}
                  className={`rounded-2xl overflow-hidden bg-cover bg-center border border-line ${i === 0 ? "col-span-2 row-span-2" : ""}`}
                  style={{
                    background: mediaUrl(ph.image) ? undefined : ph.color,
                    backgroundImage: mediaUrl(ph.image) ? `url(${mediaUrl(ph.image)})` : undefined,
                  }}
                />
              ))}
            </div>
          </section>
        )}

        {/* =================================================== Notices/Contact */}
        {(notices.length > 0 || events.length > 0 || hasContact) && (
          <section>
            <SectionHead id="contact" index={num("contact")} eyebrow="Noticeboard" title="Notices, Events & Contact" color={color} />
            <div className="grid md:grid-cols-3 gap-6">
              {notices.length > 0 && (
                <Card className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted">Notices</span>
                    <Link to="/notices" className="text-[12px] font-semibold no-underline" style={{ color }}>All →</Link>
                  </div>
                  {notices.map((n, i) => (
                    <div key={i} className="py-2.5 border-b border-line last:border-b-0">
                      <div className="text-[13px] text-ink leading-snug">{n.text}</div>
                      <div className="text-[11.5px] text-muted mt-1 font-mono">{n.date}</div>
                    </div>
                  ))}
                </Card>
              )}
              {events.length > 0 && (
                <Card className="p-5">
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted block mb-3">Events</span>
                  {events.map((ev, i) => (
                    <div key={`${ev.title}-${i}`} className="flex gap-3 py-2.5 border-b border-line last:border-b-0">
                      <div className="shrink-0 w-14 rounded-lg text-center py-1.5 text-white text-[11px] font-bold leading-tight" style={{ background: color }}>{ev.date}</div>
                      <div>
                        <div className="text-[13px] font-semibold text-ink leading-snug">{ev.title}</div>
                        {ev.venue && <div className="text-[12px] text-muted mt-0.5">{ev.venue}</div>}
                      </div>
                    </div>
                  ))}
                </Card>
              )}
              {hasContact && (
                <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(150deg,${color},#042217)` }}>
                  <div aria-hidden="true" className="absolute -right-4 -bottom-6 font-serif font-bold text-[110px] leading-none text-white/[0.07] select-none">{dept.code}</div>
                  <div className="relative">
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-gold-light block mb-3">Department Office</span>
                    <div className="flex flex-col gap-2.5 text-[13.5px]">
                      {contact.office && <span>📍 {contact.office}</span>}
                      {contact.phone && <a href={`tel:${contact.phone}`} className="text-white no-underline">📞 {contact.phone}</a>}
                      {contact.email && <a href={`mailto:${contact.email}`} className="text-white no-underline">✉ {contact.email}</a>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ======================================================= CTA banner */}
        {dept.programmes?.length > 0 && (
          <section
            className="rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden border-2 border-gold shadow-2xl"
            style={{ background: `linear-gradient(120deg,#042217,${color} 120%)` }}
          >
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff 0 2px,transparent 2px 10px)" }} />
            <div className="relative max-w-2xl flex flex-col gap-4">
              <span className="text-gold-light uppercase text-[11px] font-mono tracking-widest font-bold">{cta.eyebrow || dept.admissionsStatus || "Admissions"}</span>
              <h2 className="font-serif text-[28px] sm:text-[36px] font-bold leading-tight">
                {cta.title || `Begin your journey in ${dept.name}.`}
              </h2>
              <p className="text-white/75 text-[14px] leading-relaxed">
                {cta.desc || `Applications are accepted through the college's honours admission process${dept.kicker ? ` under ${dept.kicker}` : ""}. Visit the department office on college working days for guidance.`}
              </p>
              <div className="flex flex-wrap items-center gap-4 pt-3">
                <Link to={cta.primaryHref || applyHref} className="px-6 py-3 rounded-full bg-gold-light text-green-dark font-bold text-[11.5px] uppercase tracking-wider no-underline">
                  {cta.primaryLabel || "Apply for honours"} →
                </Link>
                <Link to={cta.secondaryHref || "/contact"} className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-[11.5px] font-semibold uppercase tracking-wider no-underline">
                  {cta.secondaryLabel || "Contact the department"}
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
